import { supabase } from '../lib/supabase';
import { normalizePhone } from '../lib/phone';
import { logError } from '../lib/logError';

// URL base do dashboard para fetch de custo-por-fonte.
// Em produção (desk), use a variável VITE_DASHBOARD_URL se disponível,
// senão fallback para domínio hardcoded.
const DASHBOARD_BASE_URL = import.meta.env.VITE_DASHBOARD_URL
  || 'https://analytics.orbioncompany.com';

/**
 * Fetch do mapa de custo-por-fonte do dashboard.
 * Retorna: { "<fonte-slug>": { spend, agendamentos, comparecimentos, vendas }, "__geral__": {...} }
 * Em erro, retorna {} para não quebrar a jornada (custo fica null).
 */
export async function getCustoPorFonte() {
  try {
    // Autentica cross-origin via Bearer: o JWT de sessão Supabase do usuário logado
    // (mesmo projeto Supabase do ecossistema). Cookie não cruza domínio.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const url = `${DASHBOARD_BASE_URL}/api/custo-por-fonte`;
    const response = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      console.warn(`getCustoPorFonte HTTP ${response.status} from ${url}`);
      return {};
    }
    const data = await response.json();
    return data || {};
  } catch (error) {
    await logError(error, { fn: 'getCustoPorFonte', severity: 'warning' });
    return {};
  }
}

/**
 * Resolve nome de pessoa por ghl_user_id usando ghl_pessoa_alias.
 * Retorna pessoa_nome ou id como fallback.
 */
async function resolveNomePessoa(ghl_user_id, nomeFallback = '') {
  if (!ghl_user_id) return nomeFallback;
  try {
    const { data } = await supabase
      .from('ghl_pessoa_alias')
      .select('pessoa_nome')
      .eq('ghl_user_id', ghl_user_id)
      .maybeSingle();
    return data?.pessoa_nome || nomeFallback;
  } catch (e) {
    console.error('resolveNomePessoa error:', e);
    return nomeFallback;
  }
}

/**
 * Agrega jornada completa de um lead por ghl_opportunity_id ou telefone.
 * Retorna objeto com estrutura completa para pop-up de jornada.
 */
export async function getLeadJourney({ ghl_opportunity_id, telefone }) {
  try {
    // Normalizar telefone para comparação (canônico do ecossistema: 55+DDD+número)
    const telefoneCanonico = telefone ? normalizePhone(telefone) : null;

    // Resolver o opportunity_id por telefone quando não veio — CHAVE confiável da jornada.
    // (Filtra por telefone_canonico; NUNCA pegar um opp arbitrário.)
    if (!ghl_opportunity_id && telefoneCanonico) {
      const { data: rref } = await supabase
        .from('ghl_reunioes')
        .select('ghl_opportunity_id')
        .eq('telefone_canonico', telefoneCanonico)
        .limit(1);
      if (rref?.length) {
        ghl_opportunity_id = rref[0].ghl_opportunity_id;
      } else {
        const { data: vref } = await supabase
          .from('ghl_vendas')
          .select('ghl_opportunity_id')
          .eq('telefone_canonico', telefoneCanonico)
          .limit(1);
        if (vref?.length) ghl_opportunity_id = vref[0].ghl_opportunity_id;
      }
    }

    // Buscar o hub_lead: por opportunity_id (se o hub o tiver), senão por telefone.
    let lead = null;
    if (ghl_opportunity_id) {
      const { data } = await supabase
        .from('hub_leads')
        .select('*')
        .eq('ghl_opportunity_id', ghl_opportunity_id)
        .limit(1);
      lead = data?.[0] || null;
    }
    if (!lead && telefoneCanonico) {
      // phone_e164 é E.164 ("+55..."); casa pelos últimos 11 dígitos (DDD + número).
      const alvo = telefoneCanonico.replace(/\D/g, '').slice(-11);
      const { data } = await supabase
        .from('hub_leads')
        .select('*')
        .ilike('phone_e164', `%${alvo}%`)
        .limit(1);
      lead = data?.[0] || null;
    }

    // Se ainda não tem nada, retornar estrutura vazia
    if (!lead && !ghl_opportunity_id && !telefoneCanonico) {
      return {
        lead: null,
        origem: null,
        timeline: [],
        tempos: null,
        passagens: null,
        reunioes: [],
        venda: null,
        custo: null,
      };
    }

    // ── Buscar dados complementares ──

    // Eventos (timeline)
    const { data: eventos = [] } = ghl_opportunity_id
      ? await supabase
          .from('ghl_eventos')
          .select('*')
          .eq('ghl_opportunity_id', ghl_opportunity_id)
          .order('event_at', { ascending: true })
      : { data: [] };

    // Reuniões
    const { data: reunioes = [] } = ghl_opportunity_id
      ? await supabase
          .from('ghl_reunioes')
          .select('*')
          .eq('ghl_opportunity_id', ghl_opportunity_id)
          .order('seq', { ascending: true })
      : { data: [] };

    // Venda
    const { data: vendaList = [] } = ghl_opportunity_id
      ? await supabase
          .from('ghl_vendas')
          .select('*')
          .eq('ghl_opportunity_id', ghl_opportunity_id)
          .limit(1)
      : { data: [] };
    const venda = vendaList[0] || null;

    // Parcelas (bd_vendas_faturamento)
    const { data: faturamento = [] } = ghl_opportunity_id
      ? await supabase
          .from('bd_vendas_faturamento')
          .select('*')
          .eq('ghl_opportunity_id', ghl_opportunity_id)
          .limit(1)
      : { data: [] };
    const fat = faturamento[0] || null;

    // Linhas de receita (bd_vendas_receita)
    const { data: receita = [] } = fat
      ? await supabase
          .from('bd_vendas_receita')
          .select('*')
          .eq('venda_id', fat.id)
          .order('parcela')
      : { data: [] };

    // ── Montar resposta ──

    // Origem/campanha
    const origem = lead
      ? {
          fonte: lead.utm_source || lead.fonte || null,
          utm_source: lead.utm_source || null,
          utm_medium: lead.utm_medium || null,
          utm_campaign: lead.utm_campaign || null,
          utm_content: lead.utm_content || null,
          utm_term: lead.utm_term || null,
          ad_name: lead.ad_name || null,
          campaign_name: lead.campaign_name || null,
          form_name: lead.form_name || null,
        }
      : null;

    // Timeline: eventos ordenados
    const timeline = await Promise.all(
      eventos.map(async (e) => {
        const nomePessoa = await resolveNomePessoa(e.assigned_to, e.assigned_to);
        return {
          evento: e.evento, // agendamento, reagendamento, comparecimento, no_show, venda, venda_perdida
          quando: e.event_at,
          pessoa: nomePessoa,
          stageId: e.stage_id || null,
          payload: e.payload || {},
        };
      })
    );

    // Tempos derivados
    let tempos = null;
    if (eventos.length > 0) {
      const primeiroEvento = eventos[0].event_at;
      const agendamentoEvent = eventos.find((e) => e.evento === 'agendamento');
      const vendaEvent = eventos.find((e) => e.evento === 'venda');

      tempos = {
        criacaoAteAgendamento: agendamentoEvent
          ? Math.ceil((new Date(agendamentoEvent.event_at) - new Date(primeiroEvento)) / (1000 * 60 * 60 * 24))
          : null,
        agendamentoAteVenda: agendamentoEvent && vendaEvent
          ? Math.ceil((new Date(vendaEvent.event_at) - new Date(agendamentoEvent.event_at)) / (1000 * 60 * 60 * 24))
          : null,
      };
    }

    // Passagens
    const pessoasAssigned = new Set(eventos.map((e) => e.assigned_to).filter(Boolean));
    const passagens = {
      pessoas: pessoasAssigned.size,
      agendamentos: reunioes.length,
    };

    // Reuniões com detalhes
    const reunioesDetalhadas = await Promise.all(
      reunioes.map(async (r) => {
        const sdrNome = await resolveNomePessoa(r.sdr_ghl_user_id, r.sdr_nome || '');
        const closerNome = await resolveNomePessoa(r.closer_ghl_user_id, r.closer_nome || '');
        return {
          seq: r.seq,
          tipo_reuniao: r.reuniao_completa === true ? 'completa' : (r.tipo_reuniao || null), // qualificacao, completa, apresentacao
          tipo_agendamento: r.tipo_agendamento || null,
          comparecimento: r.comparecimento, // null, true, false
          sdr: sdrNome,
          closer: closerNome,
          data_reuniao: r.data_reuniao || null,
          hora_reuniao: r.hora_reuniao || null,
          briefing: r.descricao || null, // briefing de agendamento
          fonte: r.fonte || null,
          campos_pendentes: r.campos_pendentes || null,
        };
      })
    );

    // Venda com parcelas
    let vendaDetalhada = null;
    if (venda) {
      const closerNomeVenda = await resolveNomePessoa(venda.closer_ghl_user_id, venda.closer_nome || '');
      const sdrNomeVenda = await resolveNomePessoa(venda.sdr_ghl_user_id, venda.sdr_nome || '');

      vendaDetalhada = {
        id: venda.id,
        ghl_opportunity_id: venda.ghl_opportunity_id,
        status: venda.status, // realizada, pendente_campos, perdida
        valor_venda: venda.valor_venda || 0,
        produto: venda.produto || null,
        forma_pagamento: venda.forma_pagamento || null,
        briefing: venda.descricao || null, // briefing de venda
        vendido_em: venda.vendido_em || null,
        closer: closerNomeVenda,
        sdr: sdrNomeVenda,
        // Detalhes financeiros (bd_vendas_faturamento)
        parcelas: fat?.parcelas || null,
        valor_entrada: fat?.valor_entrada || null,
        valor_total: fat?.valor_total || null,
        // Linhas de receita
        receita: receita.map((r) => ({
          parcela: r.parcela,
          valor: r.valor,
          forma_pagamento: r.forma_pagamento,
          data_venda: r.data_venda,
          tipo_linha: r.tipo_linha,
        })),
      };
    }

    // Custo do lead
    let custoDoLead = null;
    try {
      const custoPorFonte = await getCustoPorFonte();
      const fonteLead = origem?.fonte || '__geral__';
      const metricasFonte = custoPorFonte[fonteLead] || custoPorFonte['__geral__'] || {};

      if (metricasFonte.spend && metricasFonte.agendamentos) {
        custoDoLead = 0;

        // Agendamento
        if (reunioes.length > 0) {
          custoDoLead += (metricasFonte.spend / metricasFonte.agendamentos) * reunioes.length;
        }

        // Comparecimento
        const reunioesComparecidas = reunioes.filter((r) => r.comparecimento === true).length;
        if (reunioesComparecidas > 0 && metricasFonte.comparecimentos > 0) {
          custoDoLead += (metricasFonte.spend / metricasFonte.comparecimentos) * reunioesComparecidas;
        }

        // Venda
        if (vendaDetalhada && metricasFonte.vendas > 0) {
          custoDoLead += metricasFonte.spend / metricasFonte.vendas;
        }
      }
    } catch (e) {
      await logError(e, { fn: 'getLeadJourney/custoDoLead', severity: 'warning' });
    }

    // ── Tags do CRM (payload.contact.tags dos eventos) — atuais (último evento) + histórico ──
    // As tags mudam ao longo do funil; o evento mais recente reflete o estado atual no CRM.
    const eventosComTags = eventos.filter((e) => Array.isArray(e.payload?.contact?.tags) && e.payload.contact.tags.length > 0);
    const normTags = (arr) => arr.map((t) => String(t).trim()).filter(Boolean);
    const tagsAtuais = eventosComTags.length
      ? [...new Set(normTags(eventosComTags[eventosComTags.length - 1].payload.contact.tags))]
      : [];
    const setAtuais = new Set(tagsAtuais.map((t) => t.toLowerCase()));
    const tagsHistorico = [...new Set(eventosComTags.flatMap((e) => normTags(e.payload.contact.tags)))]
      .filter((t) => !setAtuais.has(t.toLowerCase()));
    const tags = { atuais: tagsAtuais, historico: tagsHistorico };

    // ── Data de criação no CRM (leads.ghl_created_at = dateAdded do GHL) — "quando entrou no CRM" ──
    let ghlCreatedAt = null;
    try {
      if (telefoneCanonico) {
        const { data: leadRow } = await supabase
          .from('leads')
          .select('ghl_created_at')
          .eq('telefone_canonico', telefoneCanonico)
          .not('ghl_created_at', 'is', null)
          .limit(1);
        ghlCreatedAt = leadRow?.[0]?.ghl_created_at || null;
      }
    } catch (e) {
      await logError(e, { fn: 'getLeadJourney/ghlCreatedAt', severity: 'warning' });
    }

    // ── Identidade consolidada (cabeçalho de leitura rápida, sem erro de trajetória) ──
    const contatoCrm = [...eventos].reverse().find((e) => e.payload?.contact)?.payload?.contact ?? null;
    const fonteCrm = eventos.find((e) => e.payload?.source)?.payload?.source ?? null;
    const identidade = {
      empresa: contatoCrm?.companyName || null,
      score: contatoCrm?.score ?? null,
      segmento: lead?.segmento || null,
      faturamento: lead?.faturamento || null,
      cargo: lead?.cargo || null,
      fonte: fonteCrm || origem?.fonte || null,
      // "Quando entrou" prioriza a data REAL do CRM (GHL dateAdded); fallback hub/1º evento.
      criado_em: ghlCreatedAt || lead?.created_at || (eventos[0]?.event_at ?? null),
      entrou_no_crm: ghlCreatedAt,
      responsaveis: {
        agendou: reunioesDetalhadas[0]?.sdr || null,
        closer: vendaDetalhada?.closer || reunioesDetalhadas[reunioesDetalhadas.length - 1]?.closer || null,
        vendeu: vendaDetalhada?.closer || null,
      },
    };

    return {
      lead: lead
        ? {
            full_name: lead.full_name || null,
            email: lead.email || null,
            phone_e164: lead.phone_e164 || null,
            segmento: lead.segmento || null,
            faturamento: lead.faturamento || null,
            cargo: lead.cargo || null,
            ghl_opportunity_id: lead.ghl_opportunity_id || null,
            created_at: lead.created_at || null,
          }
        : null,
      origem,
      timeline,
      tempos,
      passagens,
      reunioes: reunioesDetalhadas,
      venda: vendaDetalhada,
      custo: custoDoLead,
      tags,
      identidade,
    };
  } catch (error) {
    await logError(error, { fn: 'getLeadJourney' });
    throw error;
  }
}
